/**
 * CORS-taint probe for a 2D canvas context.
 *
 * Reads one pixel via `getImageData(0, 0, 1, 1)`. A tainted canvas throws
 * `SecurityError`; anything else that throws is a genuine readback failure
 * (canvas allocator hiccup, GPU lost, …) that callers must NOT present to
 * users as a CORS problem. The `SecurityError` narrowing moved verbatim
 * from image-editor's SVG rasterizer (its pre-PR review finding R1#5) —
 * the three-way result is the point of this helper; a boolean would
 * conflate "tainted" with "broken".
 */

export type CanvasTaintProbe =
  | { status: 'clean' }
  | { status: 'tainted'; error: DOMException }
  | { status: 'error'; error: unknown };

/** Takes the CONTEXT (not the canvas): every consumer already holds one,
 *  and probing through a fresh `getContext` call could not distinguish
 *  "tainted" from "context unavailable". */
// Legacy DOMException.SECURITY_ERR, inlined: environments whose DOMException
// lacks the static constant (happy-dom among them) would otherwise make
// `err.code === DOMException.SECURITY_ERR` compare undefined === undefined —
// classifying EVERY DOMException as tainted. The spec pins the value at 18
// forever, so the literal is browser-identical to the original check.
const SECURITY_ERR_CODE = 18;

export function probeCanvasTaint(ctx: CanvasRenderingContext2D): CanvasTaintProbe {
  try {
    ctx.getImageData(0, 0, 1, 1);
    return { status: 'clean' };
  } catch (err) {
    const isSecurityError =
      err instanceof DOMException &&
      (err.name === 'SecurityError' || err.code === SECURITY_ERR_CODE);
    if (isSecurityError) {
      return { status: 'tainted', error: err };
    }
    return { status: 'error', error: err };
  }
}
